import { z } from "zod";
import { assertIsoDate } from "../shared/dates";

export const MAX_PARSE_TEXT_CHARS = 2_000_000;
export const MAX_TRANSACTIONS = 5_000;
export const MAX_PROJECTION_DAYS = 30 * 366;
export const MAX_PDF_BYTES = 25 * 1024 * 1024;
export const MAX_CSV_BYTES = 3 * 1024 * 1024;

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
  .refine((value) => {
    try {
      assertIsoDate(value);
      return true;
    } catch {
      return false;
    }
  }, "Expected a valid calendar date");

export const transactionSchema = z.object({
  booking_date: isoDate,
  value_date: isoDate,
  text: z.string().max(1_000),
  amount: z.number().min(-1_000_000_000).max(1_000_000_000),
  source: z.string().max(64).default("csv"),
});

export const parseTextBodySchema = z.object({
  text: z.string().min(1).max(MAX_PARSE_TEXT_CHARS),
});

export const calculateParamsSchema = z.object({
  bausparsumme: z.number().positive().max(100_000_000),
  annual_interest_rate_pct: z.number().min(-10).max(100),
  zinsfaktor: z.number().positive().max(10_000_000),
  divisor_per_bausparsumme: z.number().positive().max(1),
  target_bwz: z.number().nonnegative().max(10_000_000),
  min_savings_ratio_pct: z.number().min(0).max(100),
  projection_end: isoDate,
  monthly_payment: z.number(),
  monthly_day: z.number().int().min(1).max(31),
  yearly_payment_1: z.number(),
  yearly_month_1: z.number().int().min(1).max(12),
  yearly_day_1: z.number().int().min(1).max(31),
  yearly_payment_2: z.number(),
  yearly_month_2: z.number().int().min(1).max(12),
  yearly_day_2: z.number().int().min(1).max(31),
  annual_fee: z.number(),
  annual_fee_month: z.number().int().min(1).max(12),
  annual_fee_day: z.number().int().min(1).max(31),
  pay_now_amount: z.number(),
  pay_now_date: isoDate.optional(),
  as_of_date: isoDate.optional(),
});

export const calculateBodySchema = z
  .object({
    transactions: z.array(transactionSchema).min(1).max(MAX_TRANSACTIONS),
    params: calculateParamsSchema,
  })
  .superRefine(({ transactions, params }, ctx) => {
    const earliest = transactions.reduce(
      (value, tx) => (tx.value_date < value ? tx.value_date : value),
      transactions[0].value_date,
    );
    const start = Date.parse(`${earliest}T00:00:00Z`);
    const end = Date.parse(`${params.projection_end}T00:00:00Z`);
    const durationDays = Math.round((end - start) / 86_400_000);

    if (durationDays < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["params", "projection_end"],
        message: "Projection end must not be before the first transaction",
      });
    } else if (durationDays > MAX_PROJECTION_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["params", "projection_end"],
        message: "Projection period must not exceed 30 years",
      });
    }
  });

export const suggestParamsBodySchema = z.object({
  transactions: z.array(transactionSchema).min(1).max(MAX_TRANSACTIONS),
});
